import { ReactNode, ComponentType, useSyncExternalStore } from 'react';
import type { DialogInstance } from './DialogInstance';

export interface DialogComponentProps<TResponse> {
  onClose: (value: TResponse) => void;
}

/** Props a component passed directly to openDialog may use; onCancel resolves with the fallback (or null) */
export interface DismissableDialogProps<TResponse> extends DialogComponentProps<TResponse> {
  onCancel: () => void;
}

interface DialogOptions<TResponse> {
  title?: string;
  /** Accessible name for the dialog. Use when there is no title, e.g. when the content renders its own heading */
  ariaLabel?: string;
  /** Resolved when the dialog is dismissed (Escape or `dismiss`). Without it, dismissing resolves null */
  fallbackValue?: TResponse;
}

interface ContentDialogOptions<TResponse> extends DialogOptions<TResponse> {
  /** `close` resolves with a value, `dismiss` resolves with the fallback (or null), same as pressing Escape */
  content: (close: (value: TResponse) => void, dismiss: () => void) => ReactNode;
}

type DialogComponent<TResponse> = ComponentType<DismissableDialogProps<TResponse>>;
type WithFallback<TOptions, TResponse> = TOptions & { fallbackValue: TResponse };

// The open dialogs live at module level so openDialog also works outside React (thunks, services).
// DialogProvider renders them through useDialogStack.
let stack: DialogInstance[] = [];
const listeners = new Set<() => void>();
let nextId = 0;

function setStack(next: DialogInstance[]) {
  stack = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useDialogStack() {
  return useSyncExternalStore(subscribe, () => stack);
}

export function closeDialog(dialog: DialogInstance, value: unknown) {
  dialog.resolve(value);
  setStack(stack.filter(x => x !== dialog));
}

/**
 * Opens a dialog and resolves when it closes. Works inside and outside React,
 * e.g. `if (!await openDialog(ConfirmDialog, { fallbackValue: false })) return;` in a thunk.
 * Nothing is shown unless a DialogProvider is mounted.
 * The dialog is a Paper with an optional DialogTitle; content should render its own DialogContent and DialogActions.
 */
export function openDialog<TResponse>(options: WithFallback<ContentDialogOptions<TResponse>, TResponse>): Promise<TResponse>;
export function openDialog<TResponse>(options: ContentDialogOptions<TResponse>): Promise<TResponse | null>;
export function openDialog<TResponse>(
  component: DialogComponent<TResponse>,
  options: WithFallback<DialogOptions<TResponse>, TResponse>,
): Promise<TResponse>;
export function openDialog<TResponse>(
  component: DialogComponent<TResponse>,
  options?: DialogOptions<TResponse>,
): Promise<TResponse | null>;
export function openDialog<TResponse>(
  componentOrOptions: DialogComponent<TResponse> | ContentDialogOptions<TResponse>,
  componentOptions?: DialogOptions<TResponse>,
): Promise<TResponse | null> {
  // memo/forwardRef components are objects, so check for the options shape rather than typeof function
  const { title, ariaLabel, content, fallbackValue = null }: ContentDialogOptions<TResponse | null> =
    'content' in componentOrOptions
      ? componentOrOptions
      : { ...componentOptions, content: componentContent(componentOrOptions) };

  return new Promise((resolve) => {
    const dialog: DialogInstance = {
      id: String(nextId++),
      title,
      ariaLabel,
      fallbackValue,
      resolve,
      render: () => content(value => closeDialog(dialog, value), () => closeDialog(dialog, fallbackValue)),
    };
    setStack([...stack, dialog]);
  });
}

function componentContent<TResponse>(Component: DialogComponent<TResponse>): ContentDialogOptions<TResponse>['content'] {
  return (close, dismiss) => <Component onClose={close} onCancel={dismiss} />;
}
